import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concat, map, of, switchMap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { DossierFacturableService } from '@features/demande-remboursement/choix-dossier/services/dossier-facturable.service';
import { MesDossiersSearchFormActions } from '@features/demande-remboursement/choix-dossier/store/search/search.actions';
import { MesDossiersActions } from '@features/demande-remboursement/choix-dossier/store/mes-dossiers/mes-dossiers.actions';
import { selectRouteParams } from '@root/selectors/router.selectors';
import { MesDossiersFunctions } from '@features/demande-remboursement/choix-dossier/functions/mes-dossiers-fonctions';
import { selectRechercheFormValues } from '@features/demande-remboursement/choix-dossier/store/search/search.selectors';
import { selectMesDossierStateDossiersSelectionnesIds } from '@features/demande-remboursement/choix-dossier/store/mes-dossiers/mes-dossiers.selectors';
import { DossierFacturableSearchCriteria } from '@features/demande-remboursement/choix-dossier/models/recherche-request';

export const loadDossiers$ = createEffect(
  (
    actions$ = inject(Actions),
    dossierFacturableServices = inject(DossierFacturableService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesDossiersSearchFormActions.updateSearchForm),
      withLatestFrom(store.select(selectRouteParams), store.select(selectMesDossierStateDossiersSelectionnesIds)),
      switchMap(([action, params, dossiersSelectionnes]) => {
        const dispositif = params!['type'];
        const siret = params!['id'];
        const dossierSearchCriteria = MesDossiersFunctions.searchFormToDossierSearchCriteria(
          dispositif,
          siret,
          action.form,
          dossiersSelectionnes
        );
        return getDossierFromAPI(dossierFacturableServices, dossierSearchCriteria);
      }),
    );
  },
  { functional: true },
);

export const loadDossiersWithoutUpdateCache$ = createEffect(
  (
    actions$ = inject(Actions),
    dossierFacturableServices = inject(DossierFacturableService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesDossiersSearchFormActions.updateIsDossiersEligibles),
      withLatestFrom(store.select(selectRouteParams), store.select(selectMesDossierStateDossiersSelectionnesIds), store.select(selectRechercheFormValues)),
      switchMap(([, params, dossiersSelectionnes, form]) => {
        const dispositif = params!['type'];
        const siret = params!['id'];
        const dossierSearchCriteria = MesDossiersFunctions.searchFormToDossierSearchCriteria(
          dispositif,
          siret,
          form,
          dossiersSelectionnes
        );
        return getDossierFromAPI(dossierFacturableServices, dossierSearchCriteria);
      }),
    );
  },
  { functional: true },
);

export const resetDossiers$ = createEffect(
  (
    actions$ = inject(Actions),
    dossierFacturableServices = inject(DossierFacturableService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesDossiersSearchFormActions.resetSearchForm),
      withLatestFrom(store.select(selectRechercheFormValues), store.select(selectRouteParams)),
      switchMap(([, form, params]) => {
        const dispositif = params!['type'];
        const siret = params!['id'];
        const dossierSearchCriteria = MesDossiersFunctions.searchFormToDossierSearchCriteria(
          dispositif,
          siret,
          form,
        );
        return getDossierFromAPI(dossierFacturableServices, dossierSearchCriteria);
      }),
    );
  },
  { functional: true },
);

function getDossierFromAPI(dossierFacturableServices: DossierFacturableService, dossierSearchCriteria: DossierFacturableSearchCriteria) {
  return concat(
    of(MesDossiersSearchFormActions.updateIsLoading({ isLoading: true })),
    dossierFacturableServices.getDossiers(dossierSearchCriteria).pipe(
      map((dossiers) => MesDossiersActions.getDossiersBySearchCriteriaSuccess({ dossiers })),
      catchError((error) => of(
        MesDossiersActions.getDossiersBySearchCriteriaFailure({
          errorMessage: error.error,
        })
      ))
    ),
    of(MesDossiersSearchFormActions.updateIsLoading({ isLoading: false }))
  );
}
